import React from 'react';

import { Alert, Box, CircularProgress, Pagination, Stack, Typography } from '@mui/material';

import type { ChainLpcLppSupplierPropertyGroup } from '~/types/services/priceParrot';

import SupplierRatioTable from './SupplierRatioTable';
import { PROPERTIES_PAGE_SIZE } from './constants';
import { errMsg } from './utils';

interface Props {
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  displayedProperties: Array<ChainLpcLppSupplierPropertyGroup>;
  visibleProperties: Array<ChainLpcLppSupplierPropertyGroup>;
  emptyMessage: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  onShowTrend: (group: ChainLpcLppSupplierPropertyGroup) => void;
}

export default function PropertiesList({
  isFetching,
  isError,
  error,
  displayedProperties,
  visibleProperties,
  emptyMessage,
  page,
  pageCount,
  onPageChange,
  onShowTrend,
}: Props) {
  return (
    <>
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load LPC-LPP supplier breakdown: {errMsg(error)}
        </Alert>
      )}

      {isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {displayedProperties.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{emptyMessage}</Typography>
          ) : (
            <>
              {visibleProperties.map((group) => (
                <SupplierRatioTable key={group.offer_bk} group={group} onShowTrend={onShowTrend} />
              ))}
              {pageCount > 1 && (
                <Stack alignItems="center" sx={{ pt: 1 }}>
                  <Pagination count={pageCount} page={page} onChange={(_, p) => onPageChange(p)} color="primary" />
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}

      {displayedProperties.length > 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>
          Showing{' '}
          <strong>
            {(page - 1) * PROPERTIES_PAGE_SIZE + 1}–{Math.min(page * PROPERTIES_PAGE_SIZE, displayedProperties.length)}
          </strong>{' '}
          of {displayedProperties.length} properties.
        </Typography>
      )}
    </>
  );
}
